/**
 * useCwFilePreview — shared hover-preview state + handlers for CW results.
 *
 * Sprint CW-G follow-up. Extracted from UnifiedFiles so the global
 * top-bar search dropdown and (eventually) the full Connected Search
 * page render identical thumbnail previews.
 *
 * Behavior matches the original UnifiedFiles implementation:
 *   - 1500ms hover debounce.
 *   - Fetches `/mod/cw/files/{id}/thumbnail` with the bearer header.
 *   - Three response shapes:
 *       application/json + { embed_url }  → Box iframe URL
 *       text/html                          → Atlassian HTML snippet, wrapped in blob
 *       image/*                            → image bytes → blob URL
 *   - Position clamped to viewport on render.
 *   - Skips when the hit is a folder or has no thumbnail_url.
 */
import { useCallback, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';

export interface CwPreviewState {
  id: string;
  name: string;
  x: number;
  y: number;
  blobUrl?: string;
  embedUrl?: string;
  /** Sprint CW-IS-W2 TKT-3 — text-only preview for in-system hits. */
  previewText?: string;
  previewMeta?: Record<string, string>;
}

/** Minimal shape the preview needs. CwSearchHit satisfies this. */
export interface PreviewableHit {
  id: string;
  file_name: string;
  is_folder: boolean;
  thumbnail_url?: string | null;
  /** CW-IS-W2 — in-system hits supply preview content inline. */
  preview_text?: string | null;
  preview_meta?: Record<string, string> | null;
}

const HOVER_DELAY_MS = 1500;

export function useCwFilePreview() {
  const [preview, setPreview] = useState<CwPreviewState | null>(null);
  const mousePos = useRef({ x: 0, y: 0 });
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onMouseEnter = useCallback(
    <H extends PreviewableHit>(hit: H, e: { clientX: number; clientY: number }) => {
      if (hit.is_folder) return;
      // CW-IS-W2 TKT-3 — in-system hits (CM page, DM document) carry
      // their preview inline (preview_text + preview_meta). No fetch
      // needed; render the popup with what's already on the row.
      const hasInlinePreview = Boolean(hit.preview_text);
      if (!hit.thumbnail_url && !hasInlinePreview) return;
      mousePos.current = { x: e.clientX, y: e.clientY };

      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = setTimeout(async () => {
        const initial: CwPreviewState = {
          id: hit.id,
          name: hit.file_name,
          x: mousePos.current.x + 16,
          y: Math.max(mousePos.current.y - 150, 10),
          ...(hit.preview_text ? { previewText: hit.preview_text } : {}),
          ...(hit.preview_meta ? { previewMeta: hit.preview_meta } : {}),
        };
        setPreview(initial);

        // Inline-preview hits skip the thumbnail fetch entirely.
        if (hasInlinePreview && !hit.thumbnail_url) return;

        try {
          const token = useAuthStore.getState().accessToken;
          const resp = await fetch(`/api/v1/mod/cw/files/${hit.id}/thumbnail`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (!resp.ok) return;
          const contentType = resp.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await resp.json();
            if (data.embed_url) {
              setPreview((prev) =>
                prev?.id === hit.id ? { ...prev, embedUrl: data.embed_url as string } : prev,
              );
            }
          } else if (contentType.includes('text/html')) {
            const htmlText = await resp.text();
            const htmlBlob = new Blob([htmlText], { type: 'text/html' });
            const embedUrl = URL.createObjectURL(htmlBlob);
            setPreview((prev) =>
              prev?.id === hit.id ? { ...prev, embedUrl } : prev,
            );
          } else {
            const blob = await resp.blob();
            const blobUrl = URL.createObjectURL(blob);
            setPreview((prev) =>
              prev?.id === hit.id ? { ...prev, blobUrl } : prev,
            );
          }
        } catch {
          /* preview unavailable */
        }
      }, HOVER_DELAY_MS);
    },
    [],
  );

  const onMouseMove = useCallback((e: { clientX: number; clientY: number }) => {
    mousePos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const onMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setPreview(null);
  }, []);

  return { preview, onMouseEnter, onMouseMove, onMouseLeave };
}
